import * as moment from "moment";
// The MTS section's light/dark preference.
//
// It lives here, beside the stylesheet the whole section imports, because the
// two pages have to agree on it: the drill-down is reached by clicking a date,
// and a theme that reset on the way there would flash white halfway through a
// report. The summary page is already the section's shared home - the day
// detail's SCSS imports this folder's stylesheet for the same reason.
//
// Scoped to this section deliberately. The admin has no theme of its own, and
// a preference called "dark mode" that only some pages honour is worse than
// one that plainly belongs to the page offering it.
const KEY = "mts.theme";

// localStorage rather than sessionStorage: a display preference should outlast
// the tab. Every access is guarded - it throws outright in a browser with site
// data blocked, and losing the theme must never take the report down with it.
export function mtsDarkMode(): boolean {
  try {
    return localStorage.getItem(KEY) === "dark";
  } catch {
    return false;
  }
}

export function setMtsDarkMode(dark: boolean): void {
  try {
    localStorage.setItem(KEY, dark ? "dark" : "light");
  } catch {
    // A preference that cannot be stored still applies for this page view.
  }
}

// MTS_MAX_RANGE_DAYS is the longest range the API will accept - see
// maxRangeDays in mtsReportSvc.go. It lives beside the theme helper because
// both MTS pages need it and neither owns the other.
//
// The cost of a report is the number of rows in its range, so the ceiling is
// what stops the landing page timing out. The presets all fit inside it, but
// the calendar lets a range be drawn by hand, and a user who picks three
// months would otherwise get a flat error for using the control the way it
// appears to work.
export const MTS_MAX_RANGE_DAYS = 7;

// clampMtsRange shortens an over-long range, keeping its END.
//
// The recent end is what a report is almost always about, so trimming the
// start is the least surprising way to make the selection valid. Returns the
// range unchanged when it already fits, so the caller can tell whether it
// clamped by comparing the start.
export function clampMtsRange(startDate: any, endDate: any): any {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate);
  const earliest = end.clone().startOf("day").subtract(MTS_MAX_RANGE_DAYS - 1, "days");
  return {
    startDate: start.isBefore(earliest) ? earliest : moment(startDate),
    endDate: endDate,
  };
}

// mtsCanView is the one place these pages decide whether to render.
//
// It mirrors the API exactly: IsApplicable grants the /mts endpoints to
// role == "SAPAdmin" and to nothing else, so widening this would only move the
// refusal from the page to the request. The app's own IsSAPAdmin() helper is
// deliberately NOT used - it accepts SAPUser and RiskAdmin too, which the
// backend does not.
//
// Wrapped, because the read has two failure modes that look like "not
// permitted" and are not:
//
//   - sessionStorage is PER TAB, so a page opened in a fresh tab has no
//     currentUser at all.
//   - a malformed value makes JSON.parse THROW, and a throw in a class field
//     initialiser aborts the constructor, so the component never renders and
//     the failure looks like a blank page rather than a parse error.
export function mtsCanView(): boolean {
  try {
    const raw = sessionStorage.getItem("currentUser");
    if (!raw) {
      return false;
    }
    const user = JSON.parse(raw) || {};
    return user.role === "SAPAdmin";
  } catch {
    return false;
  }
}
